import React from "react";
import { Loader2 } from "lucide-react";
import { ReadableNavButton, StickerButton } from "./Sticker";

function GoogleIcon() {
  return (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

const OAUTH_BTN_CLASS =
  "w-full min-h-[56px] px-7 py-4 !text-[15px] !font-medium gap-2.5 rounded-btn justify-center";

export default function SocialAuthButtons({
  onGoogle,
  onApple,
  loading,
  loadingProvider = null,
  compact = false,
  stacked = false,
  readable = false,
  ...rest
}) {
  const layout = stacked || compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2";
  const googleLabel =
    loading && loadingProvider === "google" ? "Signing in with Google…" : "Continue with Google";
  const appleLabel =
    loading && loadingProvider === "apple" ? "Signing in with Apple…" : "Continue with Apple";

  if (readable) {
    return (
      <div className={`grid gap-3 ${layout}`} {...rest}>
        <ReadableNavButton
          type="button"
          disabled={loading}
          aria-busy={loading && loadingProvider === "google"}
          onClick={onGoogle}
          className={OAUTH_BTN_CLASS}
          data-testid="google-sign-in-btn"
        >
          {loading && loadingProvider === "google" ? (
            <Loader2 className="w-5 h-5 shrink-0 animate-spin motion-reduce:animate-none" strokeWidth={2.5} aria-hidden="true" />
          ) : (
            <GoogleIcon />
          )}
          {googleLabel}
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          variant="night"
          disabled={loading}
          aria-busy={loading && loadingProvider === "apple"}
          onClick={onApple}
          className={OAUTH_BTN_CLASS}
          data-testid="apple-sign-in-btn"
        >
          {loading && loadingProvider === "apple" ? (
            <Loader2 className="w-5 h-5 shrink-0 animate-spin motion-reduce:animate-none" strokeWidth={2.5} aria-hidden="true" />
          ) : (
            <AppleIcon />
          )}
          {appleLabel}
        </ReadableNavButton>
      </div>
    );
  }

  const size = compact ? "md" : "lg";

  return (
    <div className={`grid gap-3 ${layout}`} {...rest}>
      <StickerButton
        color="white"
        size={size}
        type="button"
        disabled={loading}
        aria-busy={loading && loadingProvider === "google"}
        onClick={onGoogle}
        className="w-full justify-center gap-2.5"
        data-testid="google-sign-in-btn"
      >
        {loading && loadingProvider === "google" ? (
          <Loader2 className="w-5 h-5 shrink-0 animate-spin motion-reduce:animate-none" strokeWidth={2.5} aria-hidden="true" />
        ) : (
          <GoogleIcon />
        )}
        {googleLabel}
      </StickerButton>
      <StickerButton
        color="ink"
        size={size}
        type="button"
        disabled={loading}
        aria-busy={loading && loadingProvider === "apple"}
        onClick={onApple}
        className="w-full justify-center gap-2.5"
        data-testid="apple-sign-in-btn"
      >
        {loading && loadingProvider === "apple" ? (
          <Loader2 className="w-5 h-5 shrink-0 animate-spin motion-reduce:animate-none" strokeWidth={2.5} aria-hidden="true" />
        ) : (
          <AppleIcon />
        )}
        {appleLabel}
      </StickerButton>
    </div>
  );
}
